import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Tag, ExternalLink, TrendingUp, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { api, formatError } from '../lib/api';
import { toast } from 'sonner';

export default function TradeDeals() {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.listTradeDeals().then((r) => setDeals(r.data)).catch((e) => toast.error(formatError(e))).finally(() => setLoading(false));
  }, []);

  if (!user) return <Navigate to="/" replace />;
  if (user && user.role !== 'tradie') return <Navigate to="/" replace />;

  const filteredDeals = filter === 'all' ? deals : deals.filter((d) => d.categories.includes(filter));

  const claimDeal = async (deal) => {
    try {
      await api.clickTradeDeal(deal.id);
      window.open(deal.url, '_blank', 'noopener,noreferrer');
      // update local click count optimistically
      setDeals((prev) => prev.map((d) => (d.id === deal.id ? { ...d, click_count: (d.click_count || 0) + 1 } : d)));
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const uniqueCats = Array.from(new Set(deals.flatMap((d) => d.categories))).sort();

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="trade-deals-page">
      <Header />
      <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D96C4E]">Perks · tradies only</div>
            <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A] md:text-5xl">
              Deals & trade accounts
            </h1>
            <p className="mt-3 max-w-2xl text-[#5C5B5A]">
              Curated trade discounts with Perth suppliers. No paid rankings — we only feature suppliers real
              tradies use. Click a deal and we'll open the sign-up in a new tab. Real Tradies stays free forever.
            </p>
          </div>
          <div className="flex items-center gap-2 border border-[#1A362D] bg-[#1A362D]/[0.04] px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#1A362D]">
            <Sparkles size={14} /> Free forever for tradies
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="deal-filter-row">
          <button
            onClick={() => setFilter('all')}
            data-testid="deal-filter-all"
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] ${
              filter === 'all' ? 'border-[#1A362D] bg-[#1A362D] text-white' : 'border-[#E5E4E0] bg-white text-[#5C5B5A]'
            }`}
          >
            All ({deals.length})
          </button>
          {uniqueCats.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`deal-filter-${c}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] ${
                filter === c ? 'border-[#1A362D] bg-[#1A362D] text-white' : 'border-[#E5E4E0] bg-white text-[#5C5B5A]'
              }`}
            >
              {c.replace('_', ' ')}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-10 text-sm text-[#8A8988]">Loading deals...</div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-px border border-[#E5E4E0] bg-[#E5E4E0] sm:grid-cols-2 lg:grid-cols-3" data-testid="deal-grid">
            {filteredDeals.map((d) => (
              <div key={d.id} className="flex flex-col justify-between bg-white p-6 transition-colors hover:bg-[#F3F2EE]" data-testid={`deal-card-${d.id}`}>
                <div>
                  <div
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-white"
                    style={{ backgroundColor: d.brand_color }}
                  >
                    <Tag size={11} /> {d.brand}
                  </div>
                  <h3 className="mt-4 font-heading text-xl font-bold text-[#1C1B1A]">{d.title}</h3>
                  <p className="mt-2 text-sm text-[#5C5B5A]">{d.tagline}</p>
                </div>
                <div>
                  <div className="mt-6 border-t border-[#E5E4E0] pt-4">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8988]">Discount</div>
                    <div className="mt-0.5 font-heading text-lg font-bold text-[#1A362D]">{d.discount}</div>
                  </div>
                  <button
                    onClick={() => claimDeal(d)}
                    data-testid={`deal-cta-${d.id}`}
                    className="mt-4 flex w-full items-center justify-center gap-2 border border-[#1C1B1A] bg-[#1C1B1A] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1A362D]"
                  >
                    {d.cta} <ExternalLink size={14} />
                  </button>
                  {(d.click_count || 0) > 0 && (
                    <div className="mt-2 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8A8988]">
                      <TrendingUp size={11} /> {d.click_count} Perth tradies claimed
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-10 text-xs text-[#8A8988]">
          Real Tradies may receive a referral fee if you sign up. This never affects your price and never affects our
          rankings — we don't rank tradies. Suppliers featured here are chosen by us for genuine trade value.
        </p>
      </div>
    </div>
  );
}
